import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Gift, Calendar, MapPin, Heart, Sparkles, ShoppingBag, CheckCircle2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ZButton from "@/components/ZButton";
import { ZInput, ZTextarea } from "@/components/ZInput";
import Modal from "@/components/Modal";
import TermsConsent from "@/components/TermsConsent";
import ShareCelebrationModal from "@/components/ShareCelebrationModal";
import GuestClaimPrompt from "@/components/GuestClaimPrompt";

const TEAL = "#01696F";
const ORANGE = "#DA7101";

export default function PublicGiftListPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // currently focused item
  const [mode, setMode] = useState("reservation"); // "reservation" | "contribution"
  const [form, setForm] = useState({ guest_name: "", guest_email: "", message: "", amount: "", anonymous: false, accept: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/gift-lists/${slug}`);
        setList(r.data);
      } catch (e) {
        toast.error(errMsg(e));
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const itemsByCategory = useMemo(() => {
    const map = {};
    (list?.items || []).forEach((it) => { (map[it.category] = map[it.category] || []).push(it); });
    return map;
  }, [list]);

  const openItem = (it, m) => {
    setActive(it);
    setMode(m);
    setDone(false);
    setForm({
      guest_name: user?.name || "",
      guest_email: user?.email || "",
      message: "",
      amount: m === "contribution" && it.contrib_style === "single" ? String(it.price.toFixed(0)) : "",
      anonymous: false,
      accept: false,
    });
  };

  const submit = async () => {
    if (!active) return;
    if (mode === "reservation" && !form.guest_name.trim()) { toast.error("Votre nom est requis."); return; }
    if (mode === "contribution" && (!form.amount || Number(form.amount) <= 0)) { toast.error("Montant invalide."); return; }
    if (!form.accept) { toast.error("Vous devez accepter les CGV et la politique de confidentialité."); return; }
    setSubmitting(true);
    try {
      const endpoint = `/public/gift-lists/${slug}/items/${active.id}/${mode === "contribution" ? "contribute" : "reserve"}`;
      const payload = mode === "contribution"
        ? { amount: Number(form.amount), guest_name: form.guest_name.trim() || null, guest_email: form.guest_email.trim() || null, message: form.message.trim() || null, anonymous: form.anonymous }
        : { guest_name: form.guest_name.trim(), guest_email: form.guest_email.trim() || null, message: form.message.trim() || null, anonymous: form.anonymous };
      const r = await api.post(endpoint, payload);
      // Update item in list
      setList((prev) => ({
        ...prev,
        items: prev.items.map((it) => it.id === active.id ? r.data.item : it),
      }));
      setDone(true);
      toast.success(mode === "contribution" ? "Merci pour votre contribution ❤️" : "Cadeau réservé. Merci !");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ padding: 24 }}>Chargement…</div>;
  if (!list) return <div style={{ padding: 24 }}>Liste introuvable.</div>;

  return (
    <div style={{ background: "#FAFAF7", minHeight: "100vh", paddingBottom: 60 }}>
      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL}dd 100%)`, color: "white", padding: "32px 20px 28px", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.18)", padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
          <Gift size={12} /> LISTE DE CADEAUX
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.2 }} data-testid="public-list-title">{list.title}</h1>
        {list.event && (
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", fontSize: 13, opacity: 0.95, marginTop: 4 }}>
            <span><Calendar size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />{list.event.title}</span>
            {list.event.location && <span><MapPin size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />{list.event.location}</span>}
          </div>
        )}
        {list.welcome_message && (
          <p style={{ marginTop: 14, fontSize: 14, opacity: 0.95, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>{list.welcome_message}</p>
        )}
        <div style={{ marginTop: 18 }}>
          <button onClick={() => setShareOpen(true)} data-testid="share-list-btn"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: "white", color: TEAL, border: "none",
              padding: "10px 18px", borderRadius: 999, fontSize: 13, fontWeight: 700,
              cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            }}>
            <Share2 size={15} /> Partager cette liste
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: "0 auto", padding: "20px 16px" }}>
        {/* Render every category that has items — including custom ones added by the organizer
            that aren't part of the list.categories array. */}
        {(() => {
          const ordered = [
            ...list.categories,
            ...Object.keys(itemsByCategory).filter((c) => !list.categories.includes(c)),
          ];
          const sections = ordered
            .map((cat) => [cat, itemsByCategory[cat] || []])
            .filter(([, items]) => items.length > 0);
          if (sections.length === 0) {
            return (
              <div style={{ textAlign: "center", padding: "40px 20px", color: "#5B6671", fontSize: 14 }} data-testid="empty-list">
                Cette liste ne contient pas encore de cadeaux.
              </div>
            );
          }
          return sections.map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 22 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: TEAL, margin: "0 0 10px", borderLeft: `4px solid ${ORANGE}`, paddingLeft: 10 }} data-testid={`section-${cat}`}>
                {cat}
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                {items.map((it) => <PublicItemCard key={it.id} item={it} onReserve={() => openItem(it, "reservation")} onContribute={() => openItem(it, "contribution")} />)}
              </div>
            </div>
          ));
        })()}

        {list.footer_message && (
          <div style={{ background: "white", borderRadius: 14, padding: 18, marginTop: 24, fontSize: 14, color: "#5B6671", lineHeight: 1.6, fontStyle: "italic", textAlign: "center", border: `1px dashed ${ORANGE}` }}>
            {list.footer_message}
          </div>
        )}

        {/* Branding */}
        <div style={{ marginTop: 30, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0F1418", letterSpacing: -0.3 }}>
            <span style={{ color: ORANGE }}>z</span>events
          </div>
          <div style={{ fontSize: 9, letterSpacing: 1.5, fontWeight: 600, marginTop: 2 }}>BY ALO FINANCES</div>
        </div>
      </div>

      <Modal open={!!active && !done} onClose={() => setActive(null)}
        title={mode === "contribution" ? "Contribuer à ce cadeau" : "Réserver ce cadeau"}>
        {active && (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ background: "#F3F4F6", padding: 10, borderRadius: 8, fontSize: 13 }}>
              <strong>{active.name}</strong>
              {active.price > 0 && <span style={{ float: "right", color: ORANGE, fontWeight: 700 }}>{active.price.toFixed(0)} €</span>}
            </div>
            {mode === "contribution" && (
              <>
                {active.contrib_style === "pooled" && (
                  <div style={{ fontSize: 12, color: "#5B6671" }}>
                    Déjà collecté : <b>{active.raised.toFixed(0)} €</b> sur {active.price.toFixed(0)} € — il reste <b>{Math.max(0, active.price - active.raised).toFixed(0)} €</b>.
                  </div>
                )}
                <ZInput label={`Montant${active.contrib_style === "single" ? " (prix fixe)" : ""} (€) *`} type="number"
                  value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  disabled={active.contrib_style === "single"} data-testid="gift-contribute-amount" />
              </>
            )}
            <ZInput label={mode === "reservation" ? "Votre nom *" : "Votre nom"} value={form.guest_name}
              onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="gift-name" />
            <ZInput label="Votre e-mail (pour le reçu)" type="email" value={form.guest_email}
              onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="gift-email" />
            <ZTextarea label="Petit mot (optionnel)" rows={2} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="gift-message" />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#5B6671" }}>
              <input type="checkbox" checked={form.anonymous}
                onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} data-testid="gift-anonymous" />
              Apparaître en anonyme
            </label>
            <TermsConsent checked={form.accept} onChange={(v) => setForm({ ...form, accept: v })} testId="gift-terms-consent" />
            <ZButton onClick={submit} loading={submitting} disabled={!form.accept} block data-testid="gift-submit-btn">
              {mode === "contribution" ? "Confirmer ma contribution" : "Confirmer ma réservation"}
            </ZButton>
          </div>
        )}
      </Modal>

      <Modal open={!!active && done} onClose={() => { setActive(null); setDone(false); }}
        title={mode === "contribution" ? "Merci pour votre contribution 💛" : "Cadeau réservé 🎁"}>
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <CheckCircle2 size={56} color={ORANGE} style={{ margin: "0 auto 12px" }} />
          <p style={{ fontSize: 14, color: "#5B6671", lineHeight: 1.6 }}>
            {mode === "contribution"
              ? "Votre contribution a bien été enregistrée. L'organisateur a été averti."
              : "Le cadeau est désormais réservé à votre nom. L'organisateur a été averti."}
          </p>
          {!user && form.guest_email && (
            <GuestClaimPrompt
              email={form.guest_email}
              name={form.guest_name}
              next="/engagements"
              context={mode === "contribution" ? "votre contribution" : "votre cadeau"}
            />
          )}
          <ZButton onClick={() => { setActive(null); setDone(false); }} block style={{ marginTop: 14 }} data-testid="gift-done-btn">
            Voir la liste
          </ZButton>
        </div>
      </Modal>

      <ShareCelebrationModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        kind="gift"
        entityId={list.slug}
        title={list.title}
        subtitle={list.event ? list.event.title : `Liste de ${list.organizer_name || ""}`}
        imageUrl={list.cover_image_url || list.event?.image_url || null}
      />
    </div>
  );
}

function PublicItemCard({ item, onReserve, onContribute }) {
  const canReserve = (item.mode === "reservation" || item.mode === "any") && item.status !== "reserved";
  const canContribute = (item.mode === "contribution" || item.mode === "any") && item.status !== "funded" && item.price > 0;
  const statusBadge = item.status === "reserved"
    ? { bg: "#FEF3C7", color: "#92400E", label: "Réservé" }
    : item.status === "funded"
      ? { bg: "#DCFCE7", color: "#166534", label: "Financé 100%" }
      : null;

  return (
    <div style={{ background: "white", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden", display: "flex", flexDirection: "column" }} data-testid={`public-item-${item.id}`}>
      <div style={{ position: "relative", aspectRatio: "16/10", background: "#F3F4F6", overflow: "hidden" }}>
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", background: `linear-gradient(135deg, ${TEAL}22, ${ORANGE}22)` }}>
            <Gift size={42} color={ORANGE} />
          </div>
        )}
        {statusBadge && (
          <div style={{ position: "absolute", top: 8, right: 8, background: statusBadge.bg, color: statusBadge.color, padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
            {statusBadge.label}
          </div>
        )}
      </div>
      <div style={{ padding: 12, flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#0F1418", lineHeight: 1.3, minHeight: 36 }}>{item.name}</div>
        {item.description && (
          <p style={{ fontSize: 12, color: "#5B6671", margin: "4px 0 8px", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.description}</p>
        )}
        {item.price > 0 && item.contrib_style === "pooled" && item.mode !== "reservation" && (
          <div style={{ margin: "4px 0 8px" }}>
            <div style={{ height: 6, background: "#F3F4F6", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${item.progress_pct}%`, background: ORANGE }} />
            </div>
            <div style={{ fontSize: 11, color: "#5B6671", marginTop: 4 }}>
              {item.raised.toFixed(0)}€ / {item.price.toFixed(0)}€ · {item.progress_pct}%
            </div>
          </div>
        )}
        {item.price > 0 && (item.contrib_style === "single" || item.mode === "reservation") && (
          <div style={{ fontSize: 14, fontWeight: 700, color: ORANGE, margin: "4px 0 8px" }}>{item.price.toFixed(0)} €</div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: "auto" }}>
          {canReserve && (
            <button onClick={onReserve} className="z-btn z-btn-outline" style={{ flex: 1, padding: "8px 6px", fontSize: 12, justifyContent: "center" }} data-testid={`item-reserve-btn-${item.id}`}>
              <ShoppingBag size={13} /> Je l&apos;offre
            </button>
          )}
          {canContribute && (
            <button onClick={onContribute} className="z-btn z-btn-primary" style={{ flex: 1, padding: "8px 6px", fontSize: 12, justifyContent: "center" }} data-testid={`item-contribute-btn-${item.id}`}>
              <Heart size={13} /> Contribuer
            </button>
          )}
          {item.purchase_url && (
            <a href={item.purchase_url} target="_blank" rel="noopener noreferrer" className="z-btn z-btn-ghost" style={{ padding: "8px 6px", fontSize: 12 }} data-testid={`item-external-link-${item.id}`}>
              <Sparkles size={13} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
